import React, { useCallback, useEffect, useMemo, useState } from 'react'
import MaterialTable from 'material-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMinusCircle, faPen } from '@fortawesome/free-solid-svg-icons'
import CustomAxios from '../../customComponents/customAxios'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { ROUTE_NAMES } from '../../utils/routes'
import DeleteModal from '../Modals/DeleteModal'
import { useTranslation } from 'react-i18next'
import { Reducers, ReducerUser } from '../../types/reducers'
import { usePrevious, useTablePagination } from '../../utils/hooks/common'
import { Brand } from '../../types/data/system'
import LogoDefault from '../../resources/media/images/whitelabel/login-header.png'
import CustomAxiosImages from '../../customComponents/customAxiosImages'
import * as tableLocales from '../../resources/tableLocales/tableLocales'
import { ToastError } from '../../utils/toast'
import { useDispatch } from 'react-redux'
import { setUserDefaultTablePageSize } from '../../store/actions/user'
import { AvailableTablePageSizes } from '../../utils/enum/UserActivityTypes'
import { config } from '../../config/config'
import { COLORS } from '../../styles/constants'

export interface BrandTableData {
    ID: number
    name: string
    logo: string | File
    description: string
}

interface Props {
    searchQuery: string
    onFinish?: () => void
}

const BrandsTable: React.FC<Props> = (props) => {
    const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null)
    const user = useSelector<Reducers, ReducerUser>((state) => state.user)
    const [pageSize, setPageSize] = useState(user.defaultTablePageSize)
    const [firstDataLoad, setFirstDataLoad] = useState(true)
    const [currentPage, setCurrentPage] = useState(0)
    const [totalElements, setTotalElements] = useState(0)
    const tableRef = React.useRef<any>()
    const navigate = useNavigate()
    const previousQuery = usePrevious(props.searchQuery)
    const dispatch = useDispatch()

    const { t } = useTranslation()

    const currentPageFromURL = useTablePagination('page')

    useEffect(() => {
        handlePageDataUpdate()
    }, [])

    const handlePageDataUpdate = useCallback(() => {
        const currentPageValue = currentPageFromURL > 0 ? currentPageFromURL : 0
        if (currentPageValue * pageSize <= totalElements) {
            setCurrentPage(currentPageValue)
            resetData(currentPageValue)
        }
    }, [currentPageFromURL, totalElements, pageSize])

    const handleDataCount = (totCounter: number): number => {
        setTotalElements(totCounter)
        return totCounter
    }

    const removeHandler = async (brand: Brand) => {
        try {
            await CustomAxios.delete(`brands/${brand.ID}`)
            if (brand.logo) {
                const data = new FormData()
                data.append('table', 'brands')
                data.append('object_id', brand.ID.toString())
                await CustomAxiosImages.delete('images', {
                    headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${user.token}` },
                    data,
                })
            }
            resetData(currentPage, '', '', true)
            setSelectedBrand(null)
        } catch (error) {
            console.error(error)
            ToastError(t('actionsMessages.genericError'))
        }
    }

    useEffect(() => {
        if (tableRef.current && (props.searchQuery.length > 2 || (previousQuery && previousQuery.length > 2))) {
            resetData(currentPage, '', '', true)
        }
    }, [props.searchQuery])

    const resetData = useCallback((page = 0, headerToSortBy = '', order = '', forceReload = false) => {
        if (page === currentPage && !forceReload) return
        tableRef.current.onQueryChange({ page, orderBy: headerToSortBy, orderDirection: order })
    }, [])

    const columns = useMemo(() => {
        const baseUrl = config.api.baseURL
        const columns = [
            {
                title: '',
                field: 'logo',
                render: (rowData: BrandTableData) => (
                    <div className="table-element-logo-container">
                        <img
                            src={
                                rowData.logo && typeof rowData.logo === 'string'
                                    ? `${baseUrl}/${rowData.logo}`
                                    : LogoDefault
                            }
                            alt={rowData.name}
                        />
                    </div>
                ),
            },
            {
                title: t('fields.name'),
                field: 'name',
                render: (rowData: BrandTableData) => (
                    <span
                        className="colorRed table-link-value"
                        onClick={() => navigate(`/${ROUTE_NAMES.BRANDS}/${rowData?.ID}`, { state: { isView: true } })}
                    >
                        {rowData.name}
                    </span>
                ),
            },
            {
                title: t('fields.description'),
                field: 'description',
            },
        ]
        return columns
    }, [])

    const actions = useMemo(() => {
        const actions = [
            (rowData: Brand) => ({
                icon: () => <FontAwesomeIcon icon={faPen} color={COLORS.palette.darkGrey} size="xs" />,
                onClick: () => navigate(`/${ROUTE_NAMES.BRANDS}/${rowData?.ID}`),
                tooltip: t('common.edit'),
            }),
            (rowData: Brand) => ({
                icon: () => <FontAwesomeIcon icon={faMinusCircle} color={COLORS.palette.red} size="xs" />,
                onClick: () => setSelectedBrand(rowData),
                tooltip: t('common.remove'),
            }),
        ]
        return actions
    }, [])

    return (
        <>
            {selectedBrand ? (
                <DeleteModal
                    deleteMessage={t('systemMessages.removeElementFromList', { type: selectedBrand.name })}
                    onClose={() => setSelectedBrand(null)}
                    onDelete={() => void removeHandler(selectedBrand)}
                />
            ) : null}
            <MaterialTable
                columns={columns}
                tableRef={tableRef}
                data={async (query) =>
                    new Promise((resolve) => {
                        const newOffset = pageSize + pageSize * (query.page - 1)
                        let url = 'brands'
                        url += `?limit=${pageSize}`
                        url += `&offset=${newOffset}`
                        if (
                            query.orderBy !== undefined &&
                            query.orderBy.field !== undefined &&
                            (query.orderDirection as string) !== ''
                        ) {
                            url += `&sort_by=${query.orderBy.field.toString()}&order_by=${
                                query.orderDirection[0].toUpperCase() + query.orderDirection.slice(1)
                            }`
                        }
                        if (props.searchQuery && props.searchQuery.length > 2) {
                            url += `&query=${props.searchQuery}`
                        }
                        void CustomAxios.get(url).then((response) => {
                            resolve({
                                data: response.data.data,
                                page: query.page,
                                totalCount: handleDataCount(response.data.count),
                            })
                            if (firstDataLoad && props.onFinish) {
                                props.onFinish()
                                setFirstDataLoad(false)
                            }
                        })
                    })
                }
                actions={actions}
                title=""
                options={{
                    pageSize,
                    pageSizeOptions: AvailableTablePageSizes,
                    search: false,
                    actionsColumnIndex: 3,
                }}
                onChangePage={(pageIndex) => {
                    setCurrentPage(pageIndex)
                    navigate(`/${ROUTE_NAMES.BRANDS}?page=${pageIndex}`)
                }}
                onChangeRowsPerPage={(pageSize) => {
                    dispatch(setUserDefaultTablePageSize(pageSize))
                    setPageSize(pageSize)
                    resetData()
                }}
                localization={tableLocales[user.language.split('-')[0]]}
            />
        </>
    )
}

export default BrandsTable
